import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Calculator, Download, X } from 'lucide-react'
import type { FilterOption } from './creatorBusiness'

export interface FilterDimension {
  key: string
  label: string
  options: FilterOption[]
  selected: FilterOption[]
  onToggle: (opt: FilterOption) => void
  onClear: () => void
  searchable?: boolean
}

function useClickOutside(onOutside: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside()
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [onOutside])
  return ref
}

function MultiSelect({ dim }: { dim: FilterDimension }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useClickOutside(() => setOpen(false))
  const selectedIds = useMemo(() => new Set(dim.selected.map((o) => o.id)), [dim.selected])
  const shown = useMemo(() => {
    const k = q.trim().toLowerCase()
    if (!k) return dim.options.slice(0, 200)
    return dim.options.filter((o) => o.name.toLowerCase().includes(k)).slice(0, 200)
  }, [dim.options, q])
  const count = dim.selected.length

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 rounded-r-1 border px-3 py-1.5 lf-body-sm ${
          count > 0
            ? 'border-line-strong bg-surface-soft text-fg-1'
            : 'border-line-soft bg-surface-base text-fg-2'
        } hover:bg-surface-soft`}
      >
        <span>{dim.label}</span>
        {count > 0 && (
          <span className="rounded-r-pill bg-brand-blue px-1.5 text-[11px] font-medium text-fg-inverse">
            {count}
          </span>
        )}
        <ChevronDown size={14} className="text-fg-3" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 max-h-80 w-64 overflow-hidden rounded-r-2 border border-line-soft bg-surface-base shadow-lf-pop">
          {dim.searchable && (
            <div className="border-b border-line-hairline p-2">
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="검색"
                className="w-full rounded-r-1 border border-line-soft bg-surface-base px-2 py-1 lf-body-sm text-fg-1"
              />
            </div>
          )}
          <div className="max-h-56 overflow-auto py-1">
            {shown.length === 0 && (
              <div className="px-3 py-2 lf-body-sm text-fg-3">옵션 없음</div>
            )}
            {shown.map((o) => {
              const on = selectedIds.has(o.id)
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => dim.onToggle(o)}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left lf-body-sm text-fg-1 hover:bg-surface-soft"
                >
                  <span className="truncate">{o.name}</span>
                  {on && <Check size={14} className="text-brand-pink" />}
                </button>
              )
            })}
          </div>
          {count > 0 && (
            <div className="border-t border-line-hairline p-2">
              <button
                type="button"
                onClick={dim.onClear}
                className="flex items-center gap-1 lf-caption text-fg-3 hover:text-fg-1"
              >
                <X size={12} /> 선택 해제 ({count})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export interface SortOption { value: string; label: string }

export default function FilterBar({
  dimensions,
  sort,
  sortOptions,
  onSort,
  onOpenCalculator,
  cartCount,
  onExtractData,
  extracting,
}: {
  dimensions: FilterDimension[]
  sort: string
  sortOptions: SortOption[]
  onSort: (v: string) => void
  onOpenCalculator: () => void
  cartCount: number
  onExtractData: () => void
  extracting?: boolean
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {dimensions.map((d) => (
        <MultiSelect key={d.key} dim={d} />
      ))}

      <div className="ml-auto flex items-center gap-2">
        <select
          value={sort}
          onChange={(e) => onSort(e.target.value)}
          className="rounded-r-1 border border-line-soft bg-surface-base px-2 py-1.5 lf-body-sm text-fg-1"
        >
          {sortOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={onExtractData}
          disabled={extracting}
          className="flex items-center gap-1.5 rounded-r-1 border border-line-strong bg-surface-base px-3 py-1.5 lf-body-sm font-medium text-fg-1 hover:bg-surface-soft disabled:opacity-50"
        >
          {extracting ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line-soft border-t-fg-1" />
          ) : (
            <Download size={15} />
          )}
          데이터 추출
        </button>

        <button
          type="button"
          onClick={onOpenCalculator}
          className="flex items-center gap-1.5 rounded-r-1 bg-brand-pink px-3 py-1.5 lf-body-sm font-medium text-brand-brown hover:opacity-90"
        >
          <Calculator size={15} />
          견적 계산기
          {cartCount > 0 && (
            <span className="rounded-r-pill bg-brand-brown px-1.5 text-[11px] text-fg-inverse">
              {cartCount}
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
